/**
 * Document Card Module
 * Handles document information card functionality
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);


import { fileNameDatePattern, fileNameDatePatternShort, escapeRegExp } from '../utils/regex-utils.js';

/**
 * Document class for handling document metadata and card generation
 */
export class SciDoc {
    constructor(name, url, size) {
        if (debugMode === '1') {
                console.log('[SciDoc] Constructor called with:', { name, url, size });
            }
        
        // Скопируем логику из main.js class sciDoc
        let path = url.substring(window.CLOUD_URL ? window.CLOUD_URL.length : 0).match(/^(.*\/)[^\/]+$/i);
        path = path ? path[1] : '/';

        this.cutoff = 30;
        this.dateSplitter = '-';
        this.originalPdfArray = [];
        this.originalPdfFound = false;
        this.fileName = name;
        this.filePath = path;
        
        // File extension
        this.fileExt = "";
        try { 
            const extMatch = this.fileName.match(/\.(\w+)$/);
            this.fileExt = extMatch ? extMatch[1] : "";
        } catch { }
        
        // Document type detection
        let t = path.match(/(?:\/(?:LRH |\d )?)(HCO ?Bs?|БОХС|PAB|БПО|BTB|BPL|HCO ?PLs?|HCOPL|ИП ОХС|INTLRHED|Flag Order|FLAG ORDER|Volume|VOLUME|OSA|ИД|ED(?=s)|LECTURE|Book|BOOK|Course|COURSE|Journal|JOURNAL|Checksheet|CHECKSHEET|Dict|VIDEO|ВИДЕО|HASIPL|FCPL|SECED|ADMINTAPE|DICT|AUDIO (?:BOOK|LECTURE))/);
        
        if (t && t[1]) {
            this.docTechType = (['HCOB', 'БОХС', 'PAB', 'БПО', 'ТБП', 'BTB'].includes(t[1]) ? 'Tech' : 
                              (['HCOPL', 'ИП ОХС', 'BPL', 'ED', 'FLAG ORDER', 'Flag Order', 'OSA', 'INTLRHED', 'HASIPL', 'FCPL', 'SECED', 'ADMINTAPE'].includes(t[1]) ? 'Admin' : 'Other'));
            this.docMaterialType = t[1].replace(/HCOB|БОХС/g, 'HCOB')
                                        .replace(/HCO PLs|HCOPL|ИП ОХС/g, 'HCOPL')
                                        .replace(/PAB|БПО/g, 'PAB')
                                        .replace(/BTB|ТБП/g, 'BTB')
                                        .replace(/ED|SECED|ИД|INTLRHED/g, 'ED')
                                        .replace(/FLAG ORDER|Flag Order/g, 'FO')
                                        .replace(/AUDIO LECTURE/g, 'LECTURE');
        } else {
            this.docTechType = 'Other';
            this.docMaterialType = 'Other';
        }

        // Date parsing from filename (используем глобальные паттерны если доступны)
        const fileNameDatePattern = window.fileNameDatePattern;
        const fileNameDatePatternShort = window.fileNameDatePatternShort;
        
        let m = null;
        if (fileNameDatePattern) {
            m = this.fileName.match(fileNameDatePattern);
        }
        if (m === null && fileNameDatePatternShort) { 
            m = this.fileName.match(fileNameDatePatternShort); 
        }
        
        if (m !== null) {
            this.dType = m[1] || ""; 
            this.dYear = Number(m[2] || 0); 
            this.dMonth = m[3] || ""; 
            this.dDay = Number(m[4] || 0); 
            this.dRevNum = m[7] || ""; 
            this.dIssue = m[6] || "";
            
            if (this.dDay > 31 || (this.dYear < 50 && this.dDay <= this.cutoff)) {
                let t = this.dDay;
                this.dDay = this.dYear;
                this.dYear = t;
            }
            
            this.dDay2 = this.dDay.toString().padStart(2, '0');
            
            if (this.dYear.toString().length <= 2) {
                this.dYearFull = Number((this.dYear < this.cutoff && this.dYear >= 0 ? 20 : 19).toString() + this.dYear.toString());
            } else { 
                this.dYearFull = this.dYear;
            }
            
            this.dDate = this.dYearFull.toString() + this.dateSplitter + this.dMonth + this.dateSplitter + this.dDay2;
        } else {
            if (debugMode === '1') {
                console.log(`[SciDoc] Не удалось распарсить дату из: ${this.fileName}`);
            }
            this.dType = "";
            this.dYear = 0;
            this.dMonth = "";
            this.dDay = 0;
            this.dRevNum = "";
            this.dIssue = "";
            this.dDay2 = "00";
            this.dYearFull = 0;
            this.dDate = "";
        }

        // КРИТИЧЕСКИ ВАЖНО: Создаем ссылки на документы
        this.linkDoc = "/online/" + this.filePath + this.fileName;
        this.linkPdf = (window.CLOUD_URL || "") + this.filePath + "_pdf/" + this.fileName.replace(/\.docx?$/, '.pdf');

        // Search name и title
        this.searchName = this.fileName; // упрощенная версия, можно добавить fuzzyListCleanItem если нужно
        this.title = this.fileName.replace(/\.\w+$/, '');
        
        // Word count (упрощенная версия)
        this.wordCount = this.searchName.split(/\s+/).length;
        this.scoringThreshold = (this.wordCount <= 2 ? 0.1 : (this.wordCount <= 3 ? 0.125 : (this.wordCount <= 4 ? 0.15 : 0.15)));
        
        if (debugMode === '1') {
                console.log('[SciDoc] Document created:', {
            fileName: this.fileName,
            linkDoc: this.linkDoc,
            linkPdf: this.linkPdf,
            title: this.title,
            dDate: this.dDate
        });
            }
    }
    
    /**
     * Detect document technical and material type
     */
    detectDocumentType() {
        const typeMatch = this.filePath.match(
            /(?:\/(?:LRH |\d )?)(HCO ?Bs?|БОХС|PAB|БПО|BTB|BPL|HCO ?PLs?|HCOPL|ИП ОХС|INTLRHED|Flag Order|FLAG ORDER|Volume|VOLUME|OSA|ИД|ED(?=s)|LECTURE|Book|BOOK|Course|COURSE|Journal|JOURNAL|Checksheet|CHECKSHEET|Dict|VIDEO|ВИДЕО|HASIPL|FCPL|SECED|ADMINTAPE|DICT|AUDIO (?:BOOK|LECTURE))/
        );
        
        if (typeMatch) {
            const docType = typeMatch[1];
            
            // Determine technical type
            this.docTechType = this.getTechType(docType);
            
            // Determine material type
            this.docMaterialType = this.getMaterialType(docType);
        } else {
            this.docTechType = 'Other';
            this.docMaterialType = 'Other';
        }
    }
    
    /**
     * Get technical type from document type
     */
    getTechType(docType) {
        const techTypes = ['HCOB', 'БОХС', 'PAB', 'БПО', 'ТБП', 'BTB'];
        const adminTypes = ['HCOPL', 'ИП ОХС', 'BPL', 'ED', 'FLAG ORDER', 'Flag Order', 'OSA', 'INTLRHED', 'HASIPL', 'FCPL', 'SECED', 'ADMINTAPE'];
        
        if (techTypes.includes(docType)) return 'Tech';
        if (adminTypes.includes(docType)) return 'Admin';
        return 'Other';
    }
    
    /**
     * Get material type from document type
     */
    getMaterialType(docType) {
        const typeMap = {
            'HCOB': 'HCOB', 'БОХС': 'HCOB',
            'HCO PLs': 'HCOPL', 'HCOPL': 'HCOPL', 'ИП ОХС': 'HCOPL',
            'PAB': 'PAB', 'БПО': 'PAB',
            'BTB': 'BTB', 'ТБП': 'BTB',
            'ED': 'ED', 'SECED': 'ED', 'ИД': 'ED', 'INTLRHED': 'ED',
            'HASIPL': 'HASIPL',
            'FLAG ORDER': 'FO', 'Flag Order': 'FO',
            'AUDIO LECTURE': 'LECTURE'
        };
        
        return typeMap[docType] || docType;
    }
    
    /**
     * Extract date information from filename
     */
    extractDateInfo() {
        let dateMatch = this.fileName.match(fileNameDatePattern);
        if (!dateMatch) {
            dateMatch = this.fileName.match(fileNameDatePatternShort);
        }
        
        if (dateMatch) {
            const dateStr = dateMatch[0];
            this.parseDateString(dateStr);
        } else {
            // Default date values
            this.dYear = '';
            this.dMonth = '';
            this.dDay = '';
            this.dDay2 = '';
            this.dIssue = '';
            this.searchName = this.fileName;
        }
    }
    
    /**
     * Parse date string into components
     */
    parseDateString(dateStr) {
        // Try different date formats
        const formats = [
            /(\d{2})\.(\d{1,2})\.(\d{2,4})/,      // DD.MM.YYYY
            /(\d{4})-(\d{1,2})-(\d{2})/,          // YYYY-MM-DD
            /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/,    // MM/DD/YYYY
            /(\d{4})(\d{2})(\d{2})/                // YYYYMMDD
        ];
        
        for (const format of formats) {
            const match = dateStr.match(format);
            if (match) {
                // Parse based on format
                if (format.source.includes('\\.')) {
                    // DD.MM.YYYY format
                    this.dDay = match[1];
                    this.dMonth = match[2].padStart(2, '0');
                    this.dYear = this.normalizeYear(match[3]);
                } else if (format.source.includes('-')) {
                    // YYYY-MM-DD format
                    this.dYear = match[1];
                    this.dMonth = match[2].padStart(2, '0');
                    this.dDay = match[3];
                } else if (format.source.includes('\\/')) {
                    // MM/DD/YYYY format
                    this.dMonth = match[1].padStart(2, '0');
                    this.dDay = match[2];
                    this.dYear = this.normalizeYear(match[3]);
                } else {
                    // YYYYMMDD format
                    this.dYear = match[1];
                    this.dMonth = match[2];
                    this.dDay = match[3];
                }
                
                this.dDay2 = this.dDay.padStart(2, '0');
                this.extractIssue(dateStr);
                this.generateSearchName();
                return;
            }
        }
        
        // If no format matched, set defaults
        this.dYear = '';
        this.dMonth = '';
        this.dDay = '';
        this.dDay2 = '';
        this.dIssue = '';
        this.searchName = this.fileName;
    }
    
    /**
     * Normalize year to 4 digits
     */
    normalizeYear(year) {
        if (year.length === 2) {
            const currentYear = new Date().getFullYear();
            const century = Math.floor(currentYear / 100) * 100;
            const twoDigitYear = parseInt(year);
            
            // Assume 50-99 is 1950-1999, 00-49 is 2000-2049
            if (twoDigitYear > 50) {
                return (century - 100) + twoDigitYear;
            } else {
                return century + twoDigitYear;
            }
        }
        return year;
    }
    
    /**
     * Extract issue number from date string
     */
    extractIssue(dateStr) {
        const issueMatch = dateStr.match(/[A-Z]+$/);
        if (issueMatch) {
            this.dIssue = issueMatch[0];
        } else {
            this.dIssue = '';
        }
    }
    
    /**
     * Generate search name
     */
    generateSearchName() {
        // Remove date pattern from filename
        this.searchName = this.fileName.replace(new RegExp(fileNameDatePattern, 'g'), '');
        this.searchName = this.searchName.replace(/[_-]+/g, ' ').trim();
    }
    
    /**
     * Get formatted date string
     */
    getFormattedDate() {
        if (!this.dYear) return '';
        
        const parts = [];
        if (this.dDay) parts.push(this.dDay);
        if (this.dMonth) parts.push(this.dMonth);
        if (this.dYear) parts.push(this.dYear);
        if (this.dIssue) parts.push(this.dIssue);
        
        return parts.join(this.dateSplitter);
    }
    
    /**
     * Get document metadata
     */
    getMetadata() {
        return {
            fileName: this.fileName,
            filePath: this.filePath,
            fileExt: this.fileExt,
            fileSize: this.fileSize,
            fileUrl: this.fileUrl,
            docTechType: this.docTechType,
            docMaterialType: this.docMaterialType,
            date: this.getFormattedDate(),
            searchName: this.searchName
        };
    }
    
    /**
     * Check if document is a bulletin
     */
    isBulletin() {
        return ['HCOB', 'HCOPL', 'PAB', 'BTB', 'BPL'].includes(this.docMaterialType);
    }
    
    /**
     * Check if document has date
     */
    hasDate() {
        return Boolean(this.dYear && this.dMonth && this.dDay);
    }
} 